import { useEffect, useState, useContext, useDebugValue } from "react";
import { useTranslation } from "react-i18next";

import { toMoney } from "../utils/toMoney";
import { formatDate } from "../utils/toDate";
import { sanitizeNumber } from "../utils/sanitizeNumber";

import { DataContext } from "../utils/DataContext";

import searchIcon from "../assets/search_icon.png";
import debtIcon from "../assets/debt_icon.png";
import paymentIcon from "../assets/payment_icon.png";

import RoundedTable, { TABLE_CELL_TYPES } from "./table-component/RoundedTable";
import Modal from "./Modal";
import Loader from "./Loader";
import TextDiv from "./TextDiv";
import BalanceBoxes from "./BalanceBoxes";
import InformationBox from "./InformationBox";
import CustomInput from "./CustomInput";
import Dropdown from "./Dropdown";
import CustomButton from "./CustomButton";
import SearchFilter from "./SearchFilter";
import PaymentModal from "./PaymentModal";
import { UserAuth } from "../utils/AuthContext";
import { pdf } from "@react-pdf/renderer";
import useLanguage from "../utils/useLanguage";
import TransactionStatementPDF from "./TransactionPdfTemplate";
import downloadIcon from "../assets/download-icon.svg";
import axios from "axios";
import { setDate } from "date-fns";

const BanksTransactionModal = ({
  selected,
  handleToggleTransactionsModal,
  withSubCompany,
  setShouldGetCustomers,
}) => {
  const { currentDirection } = useLanguage();
  const { t } = useTranslation("general");

  const { userData } = useContext(UserAuth);

  const {
    subCompanies,
    getSubCompanies,
    selectedSubCompany,
    handleSelectSubCompany,
  } = useContext(DataContext);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });

  const [depositeModal, setDepositeModal] = useState(false);
  const [withdrawModal, setWithdrawlModal] = useState(false);
  const [detailsModal, setDetailsModal] = useState(false);
  const [data, setData] = useState({});
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");

  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  // Search states
  const [selectedTitle, setSelectedTitle] = useState({
    title: t("Date"),
    value: "date",
  });
  const [searchQuery, setSearchQuery] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [amounts, setAmounts] = useState({
    total: selected?.totalTransactionAmount,
    balance: selected?.balance,
    received: selected?.received,
  });

  const [selectedTransactionType, setSelectedTransactionType] = useState({
    value: "",
    title: t("selectAll"),
  });

  const transactionTypeOptions = [
    { value: "debtRepayment", title: t("debtRepayment") },
    { value: "deposit", title: t("deposit") },
    { value: "withdrawal", title: t("withdrawal") },
    { value: "", title: t("selectAll") },
  ];

  const searchTitles = [
    { title: t("Date"), value: "date" },
    { title: t("amount"), value: "amount" },
    { title: t("transactionType"), value: "transactionType" },
  ];

  if (withSubCompany) {
    searchTitles.push({ title: t("subCompany"), value: "subCompany" });
  }

  const handleSearchType = async (type) => {
    if (
      type.value === "invoiceNumber" ||
      type.value === "amount" ||
      type.value === "subCompany" ||
      type.value === "bankName" ||
      type.value === "transactionType"
    ) {
      setStartDate("");
      setEndDate("");
    } else if (type.value === "date") {
      setSearchQuery("");
    } else {
      // throw error("invalid search type");
    }
    if (type.value == "transactionType") {
      setMaxAmount("");
      setMinAmount("");
    }

    setSelectedTitle(type);
  };

  const handleSearch = async (query = searchQuery) => {
    if (typeof query !== "string") {
      query = searchQuery;
    }

    let searchBy;

    if (selectedTitle?.value === "date") {
      searchBy = { from: startDate, to: endDate };
    } else if (selectedTitle?.value === "amount") {
      searchBy = sanitizeNumber(query);
    } else if (selectedTitle?.value === "transactionType") {
      searchBy = selectedTransactionType?.value;
    } else if (selectedTitle?.value === "subCompany") {
      searchBy = selectedSubCompany?.id;
    } else {
      searchBy = query;
    }

    refetch({
      searchQuery: searchBy,
      searchType: selectedTitle.value,
    });
  };

  // function to fetch all transactions for PDF generation
  const handleDownloadPDF = async () => {
    setPdfLoading(true);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/api/transaction/getTransactionByBank`,
        {
          params: {
            bankId: selected.id,
            page: 1,
            limit: 10000,
            startDate: selectedTitle.value === "date" ? startDate : "",
            endDate: selectedTitle.value === "date" ? endDate : "",
            searchType: selectedTitle.value,
            searchQuery:
              selectedTitle.value === "transactionType" ||
              selectedTitle.value === "date"
                ? ""
                : searchQuery,
            transactionType:
              selectedTitle.value === "transactionType"
                ? selectedTransactionType?.value
                : "",
            minAmount: selectedTitle.value === "amount" ? minAmount : "",
            maxAmount: selectedTitle.value === "amount" ? maxAmount : "",
            subCompanyId:
              selectedTitle.value === "subCompany"
                ? selectedSubCompany?.id
                : "",
          },
          headers: {
            "organization-id": userData?.organizationId,
            Authorization: `Bearer ${userData.token}`,
          },
        }
      );

      if (response?.data?.transactions) {
        const blob = await pdf(
          <TransactionStatementPDF
            language={currentDirection === "rtl" ? "AR" : "EN"}
            companyName={userData?.organizationName || "Company Name"}
            data={{
              customer: selected,
              transactions: response.data.transactions,
              balance: response.data.balance,
            }}
            transactions={response.data.transactions.map((transaction) => ({
              ...transaction,
              name: t(transaction.type),
              type: t(transaction.type),
            }))}
            isCustomer={false}
            bankName={selected?.name}
          />
        ).toBlob();

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${response.data?.customer?.name || "transactions"}-${
          new Date().getMonth() + 1
        }-${new Date().getDay() + 1}-${(Math.random() * 1000).toFixed(0)}.pdf`;
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 5000);
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error("PDF generation failed:", error);
    } finally {
      setPdfLoading(false);
    }
  };

  const refetch = async (params = {}) => {
    const page = params.page || pagination.currentPage;
    const limit = params.limit || 5;
    const searchQuery = params.searchQuery || "";
    const searchType = params.searchType || "";

    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/api/transaction/getTransactionByBank`,
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
            "organization-id": userData?.organizationId,
          },
          params: {
            bankId: selected.id,
            page: page,
            limit: limit,
            startDate: selectedTitle.value === "date" ? startDate : "",
            endDate: selectedTitle.value === "date" ? endDate : "",
            searchQuery:
              selectedTitle.value === "transactionType" ||
              selectedTitle.value === "date"
                ? ""
                : searchQuery,
            transactionType:
              selectedTitle.value === "date" ? null : searchQuery,
            minAmount: minAmount || "",
            maxAmount: maxAmount || "",
          },
        }
      );

      if (response.status === 200) {
        setData({
          transactions: {
            records: response.data.transactions,
            pagination: {
              currentPage: response.data.page,
              totalPages: response.data.totalPages,
              totalRecords: response.data.totalRecords,
            },
            balance: response.data.balance,
          },
        });
        setLoading(false);
        setPagination({
          currentPage: response.data.page,
          totalRecords: response.data.totalRecords,
          totalPages: response.data.totalPages,
        });
        setAmounts({
          ...amounts,
          balance: response.data.balance,
        });
      }
    } catch (error) {
      console.error("Failed to fetch transactions", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refetch({
      page: pagination.currentPage,
      limit: 5,
    });
  }, [pagination.currentPage]);

  useEffect(() => {
    if (data?.transactions) {
      setPagination({
        currentPage: data?.transactions?.pagination?.currentPage,
        totalPages: data.transactions.pagination?.totalPages,
        totalRecords: data.transactions?.pagination?.totalRecords,
      });
    }

    setAmounts({
      total: data?.transactions?.total,
      balance: data?.transactions?.balance,
      received: data?.transactions?.received,
    });
  }, [data]);

  const tableTitles = [
    {
      value: "transaction",
    },
    {
      value: "Date",
      keepOnMobile: true,
    },

    {
      value: "amount",
      keepOnMobile: true,
    },
  ];

  const tableRows = data?.transactions?.records?.map((el) => {
    const storedName = el?.name?.split("---")?.[0];

    // To translate the "addDebt"
    const notInvoiceNumber = storedName === "addDebt";

    const transactionName = t(el.type);

    const bankName = el?.bankName;

    // List of transaction.type for negative style
    const invoiceTypes = ["debtRepayment", "deposit"];

    return [
      {
        value: (
          <div className="cell ">
            <CustomButton
              variant="transparent"
              onClick={() => {
                setSelectedTransaction({ ...el, transactionName });
                setDetailsModal(true);
              }}
              content={
                <div className="o-column o-column--gap-size_100 u-padding_300">
                  <div>{transactionName}</div>
                  {bankName && (
                    <span className="t-text-mini t-bold_normal">
                      ( {bankName} )
                    </span>
                  )}
                </div>
              }
            />
          </div>
        ),
        type: TABLE_CELL_TYPES.ELEMENT,
        keepOnMobile: true,
      },
      { value: el.date, type: TABLE_CELL_TYPES.DATE, keepOnMobile: true },
      {
        value: el.amount,
        type: TABLE_CELL_TYPES.MONEY,
        sign: invoiceTypes.includes(el?.type)
          ? "positive-value"
          : "negative-value",
        bold: true,
        keepOnMobile: true,
        currency: selected?.currency || t("USD"),
      },
    ];
  });

  const TextDivStyles =
    "o-row o-row--vertical-align_center o-row--gap-size_500 ";
  const TextPStyles = "t-bold_semi-bold u-width-specific_150 u-margin_0";

  return (
    <>
      <Modal title={t("transactions")} onClose={handleToggleTransactionsModal}>
        {/* {error && <ErrorBox>{errorMessage}</ErrorBox>} */}
        <section className="o-column-row-switcher o-row--horizontal-align_space-between u-padding--top_200">
          <TextDiv
            title="bank"
            value={selected?.name}
            divClasses="o-row o-row--gap-size_300 o-row--vertical-align_center"
          />
          {userData?.userType === "orgUser" && (
            <div className="o-row o-row--gap-size_300">
              <CustomButton
                variant="green"
                title={t("deposit")}
                onClick={() => setDepositeModal((prev) => !prev)}
                icon={paymentIcon}
                iconSize={22}
              />
              <CustomButton
                variant="red"
                title={t("withdrawal")}
                onClick={() => setWithdrawlModal((prev) => !prev)}
                icon={debtIcon}
                iconSize={20}
              />
            </div>
          )}
        </section>
        {data?.length !== 0 ? (
          <div className="o-column o-column--gap-size_300">
            <label className="t-bold_semi-bold">{`${t("searchTitle")} ${t(
              selectedTitle?.title
            )}`}</label>
            <section className="o-column-row-switcher o-row--gap-size_300 o-column--vertical-align_end">
              <div className="o-row o-row--vertical-align_center o-column--vertical-align_end o-row--gap-size_300 u-width_full-width o-column--horizontal-align_space-between">
                {selectedTitle.value === "date" ? (
                  <div className="o-column-row-switcher o-column--gap-size_300 ">
                    <CustomInput
                      name="date"
                      title={t("from")}
                      type="date"
                      thick
                      onChange={(date) => {
                        setStartDate(date?.target?.value);
                      }}
                      value={formatDate(startDate)}
                      resetSearch={() => {
                        setStartDate(null);
                      }}
                    />

                    <CustomInput
                      name="date"
                      title={t("to")}
                      type="date"
                      thick
                      onChange={(date) => {
                        setEndDate(date?.target?.value);
                      }}
                      resetSearch={() => {
                        setEndDate(null);
                      }}
                      value={formatDate(endDate)}
                    />
                  </div>
                ) : selectedTitle.value === "transactionType" ? (
                  <Dropdown
                    options={[...transactionTypeOptions]}
                    selected={selectedTransactionType}
                    setSelected={setSelectedTransactionType}
                    thick
                    // nameInAr={true}
                  />
                ) : selectedTitle.value === "subCompany" ? (
                  <Dropdown
                    options={[{ id: "", nameInAr: "All" }, ...subCompanies]}
                    selected={selectedSubCompany}
                    setSelected={(subCompany) =>
                      handleSelectSubCompany(subCompany, "noRequest")
                    }
                    thick
                    nameInAr={true}
                    getData={getSubCompanies}
                  />
                ) : selectedTitle.value === "amount" ? (
                  <div className="o-row o-row--gap-size_200">
                    <CustomInput
                      title={t("from")}
                      value={minAmount}
                      onChange={(e) => {
                        setMinAmount(e.target.value);
                      }}
                      resetSearch={() => {
                        setSearchQuery(null);
                        setMinAmount("");
                      }}
                      thick
                      flex
                    />
                    <CustomInput
                      title={t("to")}
                      value={maxAmount}
                      onChange={(e) => {
                        setMaxAmount(e.target.value);
                      }}
                      resetSearch={() => {
                        setSearchQuery(null);
                        setMaxAmount("");
                      }}
                      thick
                      flex
                    />
                  </div>
                ) : (
                  <CustomInput
                    placeholder={`${t("searchTitle")} ${t(
                      selectedTitle?.value
                    )}`}
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                    }}
                    resetSearch={() => {
                      setSearchQuery(null);
                      handleSearch("");
                    }}
                    thick
                    flex
                  />
                )}
                <SearchFilter
                  options={searchTitles}
                  selected={selectedTitle}
                  setSelected={handleSearchType}
                  thick
                />
              </div>

              <CustomButton
                title={t("search")}
                onClick={handleSearch}
                icon={searchIcon}
                iconSize={15}
                classList="c-search-button"
              />
            </section>
          </div>
        ) : null}
        {loading ? (
          <Loader />
        ) : (
          <>
            {tableRows?.length ? (
              <section>
                <RoundedTable
                  titles={tableTitles}
                  rows={tableRows}
                  pagination={pagination}
                  setPagination={setPagination}
                />
                <BalanceBoxes
                  boxes={[
                    {
                      title: "balance",
                      value: amounts?.balance,
                      currency: selected?.currency || t("USD"),
                    },
                  ]}
                />
              </section>
            ) : (
              <InformationBox>{t("noTransactions")}</InformationBox>
            )}
          </>
        )}

        {depositeModal ? (
          <PaymentModal
            title="deposit"
            selected={selected}
            handleTogglePaymentModal={() => setDepositeModal((prev) => !prev)}
            modalSize="c-modal--full-height"
            transactionType="deposit"
            refetchData={refetch}
            setShouldGetCustomers={setShouldGetCustomers}
            isBank={true}
          />
        ) : null}

        {withdrawModal ? (
          <PaymentModal
            title="withdrawal"
            selected={selected}
            handleTogglePaymentModal={() => setWithdrawlModal((prev) => !prev)}
            modalSize="c-modal--full-height"
            transactionType={"withdrawal"}
            refetchData={refetch}
            setShouldGetCustomers={setShouldGetCustomers}
            isBank={true}
          />
        ) : null}

        {detailsModal ? (
          <Modal
            modalSize="c-modal--small"
            title="details"
            onClose={() => setDetailsModal(false)}
          >
            <div className="o-column o-column--gap-size_300">
              <TextDiv
                title="transaction"
                value={selectedTransaction?.transactionName}
                divClasses={TextDivStyles}
                pClasses={TextPStyles}
              />

              <TextDiv
                title="amount"
                value={`${toMoney(selectedTransaction?.amount)} ${
                  selected?.currency || t("USD")
                } `}
                divClasses={TextDivStyles}
                pClasses={TextPStyles}
              />

              <TextDiv
                title="Date"
                value={formatDate(selectedTransaction?.date)}
                divClasses={TextDivStyles}
                pClasses={TextPStyles}
              />

              {selectedTransaction?.bankName && (
                <TextDiv
                  title="bankName"
                  value={selectedTransaction?.bankName}
                  divClasses={TextDivStyles}
                  pClasses={TextPStyles}
                />
              )}

              <TextDiv
                title="notes"
                value={selectedTransaction?.notes}
                divClasses={TextDivStyles}
                pClasses={TextPStyles}
              />
            </div>
          </Modal>
        ) : null}

        {pdfLoading && <Loader />}
        <div className="o-row o-row--horizontal-align_end u-margin--top_500">
          <CustomButton
            variant="blank"
            onClick={handleDownloadPDF}
            disabled={pdfLoading}
            icon={downloadIcon}
            title={pdfLoading ? t("generatingPdf") : t("downloadStatement")}
          />
        </div>
      </Modal>
    </>
  );
};

export default BanksTransactionModal;
